/* eslint-disable react-hooks/exhaustive-deps */
import { debounce } from 'lodash';
import {
  ChangeEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Heading, HeadingType } from '../..';
import { handleTrack } from '../../../utils/tracker';
import { ButtonVariants } from '../../Atoms/Button';
import { SimpleSlider } from '../../Atoms/Carousel';
import { StickyCTA } from '../../CTA/StickyCTA';
import { BOOK_EXPERT_TEXT, FILE_NOW_TEXT } from '../../CTA/constants';
import { NAV_TYPES } from '../../cms/layout/components/header';
import { ViewMore } from '../Common/ViewMore';
import { WebinarCard } from '../Common/WebinarCard';
import WebinarsDataService from '../Services/webinars.service';
import { isItrProduct } from '../Utils';
import {
  DEFAULT_VIEW_MORE_LIMIT,
  EVENT_TYPE,
  PAST_NOT_FOUND_ERROR,
  SORT_BY,
  UPCOMING_NOT_FOUND_ERROR,
} from '../Utils/constants';
import {
  IHOMPAGE_DATA,
  IWEBINAR_DATA,
  IWEBINAR_RESPONSE,
} from '../Utils/types';
import { WebinarHero } from './WebinarHero';

const WebinarHomepage = ({ data }: { data: IHOMPAGE_DATA }): JSX.Element => {
  const {
    upcoming,
    past,
    product,
    productVisibility,
    productId,
    searchString,
  } = data;

  const defaultWebinarResponse: IWEBINAR_RESPONSE = {
    count: 0,
    results: [],
    previous: null,
    next: null,
  };

  const filterWebinarWithVideo = (pastWebinar: IWEBINAR_RESPONSE) => {
    const obj = {
      ...pastWebinar,
    };
    obj.results =
      pastWebinar?.results?.filter((webinar) => webinar.video_link) ?? [];
    return obj;
  };

  useLayoutEffect(() => {
    if (past) {
      filterWebinarWithVideo(past);
    }
  }, []);

  const [offset, setOffset] = useState(0);
  const [upcomingWebinarData, setUpcomingWebinarData] = useState(
    upcoming ?? defaultWebinarResponse
  );
  const [pastWebinarData, setPastWebinarData] = useState(
    past ?? defaultWebinarResponse
  );
  const [selectedProduct, setProduct] = useState(productId);
  const [searchText, setSearchText] = useState<string>(searchString);
  let sortBy = SORT_BY.DESCENDING;
  let viewMoreLimit = DEFAULT_VIEW_MORE_LIMIT;
  const isProductDropdownVisible = productVisibility;
  const isItr = isItrProduct({ id: data.productId });

  const pastWebinarTotalCount = pastWebinarData?.count ?? 0;
  const pastWebinarVisibleCount = pastWebinarData?.results?.length ?? 0;

  const upcomingRef = useRef<HTMLDivElement>(null);
  const pastRef = useRef<HTMLDivElement>(null);

  const scrollWebinarsToView = (hasUpcomingData: boolean) => {
    if (hasUpcomingData) {
      upcomingRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    } else {
      pastRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const onSearchChange = async (
    selectedProducts: string,
    searchTextValue: string
  ) => {
    viewMoreLimit = DEFAULT_VIEW_MORE_LIMIT;

    const upcomingList = await WebinarsDataService.filterUpcomingWebinars(
      selectedProducts,
      searchTextValue
    );
    setUpcomingWebinarData(upcomingList.data);

    const pastList = await WebinarsDataService.filterPastWebinars(
      selectedProducts,
      searchTextValue,
      sortBy,
      viewMoreLimit
    );
    const filteredData = filterWebinarWithVideo(pastList.data);
    setPastWebinarData(filteredData);
    handleTrack({
      label: `search_input_${searchTextValue}`,
    });

    const hasUpcomingData = upcomingList.data?.results.length ? true : false;
    scrollWebinarsToView(hasUpcomingData);
  };

  const onProductChange = async (e: ChangeEvent<HTMLSelectElement>) => {
    viewMoreLimit = DEFAULT_VIEW_MORE_LIMIT;
    setProduct(e.target.value);
    const selectedText = e.target.options[e.target.selectedIndex]?.text;
    const upcomingList = await WebinarsDataService.filterUpcomingWebinars(
      e.target.value,
      searchText
    );
    setUpcomingWebinarData(upcomingList.data);

    const pastList = await WebinarsDataService.filterPastWebinars(
      e.target.value,
      searchText,
      sortBy,
      DEFAULT_VIEW_MORE_LIMIT
    );
    const filteredData = filterWebinarWithVideo(pastList.data);
    setPastWebinarData(filteredData);

    handleTrack({
      label: `search_product_dropdown_${selectedText}`,
    });
    const hasUpcomingData = upcomingList.data?.results.length ? true : false;
    scrollWebinarsToView(hasUpcomingData);
  };

  const handleViewMore = async () => {
    viewMoreLimit += DEFAULT_VIEW_MORE_LIMIT;

    const viewMoreWebinars = await WebinarsDataService.filterPastWebinars(
      selectedProduct,
      searchText,
      sortBy,
      viewMoreLimit
    );

    const filteredData = filterWebinarWithVideo(viewMoreWebinars.data);
    setPastWebinarData(filteredData);
    handleTrack({ label: `viewMore_webinar_displayCount_${viewMoreLimit}` });
  };

  const handleSort = async (sortByValue: string) => {
    const webinarList = await WebinarsDataService.filterPastWebinars(
      selectedProduct,
      searchText,
      sortByValue,
      viewMoreLimit
    );
    sortBy = sortByValue;
    const filteredData = filterWebinarWithVideo(webinarList.data);
    setPastWebinarData(filteredData);
    const sortingBy =
      sortByValue === SORT_BY.ASCENDING ? 'ascending' : 'descending';
    handleTrack({ label: `sort_webinar_${sortingBy}` });
  };

  const debouncedResults = useMemo(() => {
    return debounce(onSearchChange, 300);
  }, []);

  useEffect(() => {
    const onScroll = () => setOffset(window.pageYOffset);
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const cardClick = (
    webinar: IWEBINAR_DATA,
    label: string,
    buttonText: string
  ) => {
    const redirectUrl = `${'/webinars/'}${webinar.slug}`;
    handleTrack({
      label: `${label}_${webinar.title}_${buttonText}`,
      url: redirectUrl,
    });
    window.open(redirectUrl);
  };

  const stickyCTAProp = isItr
    ? { type: NAV_TYPES.B2C }
    : { link: 'https://cleartax.in/s/pricing/' };

  return (
    <div className="relative" data-testid="webinar-homepage">
      <WebinarHero
        sticky={offset > 400 ? true : false}
        products={product}
        selectedProduct={selectedProduct}
        onProductChange={onProductChange}
        onSearchChange={() => debouncedResults(selectedProduct, searchText)}
        setSearchText={(value: string) => setSearchText(value)}
        searchText={searchText}
        showProductDropdown={isProductDropdownVisible}
      />

      <div
        className="wg-container py-8"
        data-testid="webinar-heading"
        ref={upcomingRef}
      >
        <Heading
          variant={HeadingType.h4}
          content="Our upcoming webinars"
          className="sm:text-s-28"
        />
        <p className="text-font-200 py-2">
          Stay ahead with expert-led sessions designed for enterprise leaders
        </p>
      </div>

      <div className="py-2 mb-6" data-testid="upcoming-webinar">
        {upcomingWebinarData.results?.length ? (
          upcomingWebinarData.results.length > 3 ? (
            <SimpleSlider>
              {upcomingWebinarData.results.map((upcomingWebinar) => (
                <WebinarCard
                  key={upcomingWebinar.title}
                  data={upcomingWebinar}
                  eventType={EVENT_TYPE.UPCOMING}
                  onClick={() =>
                    cardClick(
                      upcomingWebinar,
                      'card_upcoming_webinar',
                      'Register_Now'
                    )
                  }
                />
              ))}
            </SimpleSlider>
          ) : (
            <div className="wg-container">
              {upcomingWebinarData.results.map((upcomingData) => (
                <div
                  key={upcomingData.title}
                  className="w-1/3 inline-block my-4 sm:w-full"
                >
                  <WebinarCard
                    data={upcomingData}
                    eventType={EVENT_TYPE.UPCOMING}
                    onClick={() =>
                      cardClick(
                        upcomingData,
                        'card_upcoming_webinar',
                        'Register_Now'
                      )
                    }
                  />
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="wg-container text-center">
            {UPCOMING_NOT_FOUND_ERROR}
          </div>
        )}
      </div>

      <div className="wg-container py-8 mt-4" ref={pastRef}>
        <Heading
          variant={HeadingType.h4}
          content="Watch our past Webinars here"
          className="sm:text-s-28"
        />
        <p className="text-font-200 py-2">
          Revisit high-impact sessions covering critical trends, regulatory
          shifts, and transformation strategies
        </p>
        {pastWebinarData.results?.length ? (
          <>
            <div className="flex gap-2 items-center justify-end text-s-14 text-font-500 sm:hidden">
              <div>Sort by:</div>
              <select
                data-testid="sort"
                name="sort"
                className="p-2 border rounded-sm text-s-14 text-font-200"
                onChange={(e) => handleSort(e.target.value)}
              >
                <option value={SORT_BY.DESCENDING}>New to Old</option>
                <option value={SORT_BY.ASCENDING}>Old to New</option>
              </select>
            </div>

            <div className="flex py-4 flex-wrap">
              {pastWebinarData.results
                .filter((webinarHasVideo) => webinarHasVideo.video_link)
                .map((pastWebinar) => (
                  <div
                    key={pastWebinar.title}
                    className="w-1/3 inline-block my-4 sm:w-full"
                  >
                    <WebinarCard
                      data={pastWebinar}
                      eventType={EVENT_TYPE.PAST}
                      onClick={() =>
                        cardClick(pastWebinar, 'card_past_webinar', 'Learn_Now')
                      }
                    />
                  </div>
                ))}
            </div>

            {pastWebinarVisibleCount < pastWebinarTotalCount ? (
              <ViewMore onViewMoreClick={handleViewMore} text="View more" />
            ) : null}
          </>
        ) : (
          <div className="py-6 text-center">{PAST_NOT_FOUND_ERROR}</div>
        )}
      </div>
      <StickyCTA
        buttonText={isItr ? FILE_NOW_TEXT : BOOK_EXPERT_TEXT}
        buttonStyle={ButtonVariants.bluefilled}
        {...stickyCTAProp}
      />
    </div>
  );
};

export { WebinarHomepage };
